import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import RoleWalkthrough from "@/components/RoleWalkthrough";
import { LayoutDashboard, Package, ClipboardList, Tag, LogOut, ExternalLink, Settings, Sparkles, ShoppingCart, Banknote, Users, BadgeCheck, ShieldCheck, ScrollText, BarChart3, HelpCircle, Truck, MessageSquareText } from "lucide-react";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  // Spec §1 — 5-minute idle auto-logout at the register.
  // Applies for owner, manager, associate, driver — hook is a no-op for customers.
  useIdleLogout({ idleMinutes: 5 });

  const isOwner = user?.role === "owner" || user?.role === "admin";

  const links = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/admin/quick-add", icon: Sparkles, label: "Quick Add", highlight: true },
    { to: "/admin/products", icon: Package, label: "Products" },
    { to: "/admin/orders", icon: ClipboardList, label: "Orders" },
    { to: "/admin/reports", icon: BarChart3, label: "Reports", highlight: true },
    { to: "/admin/supplier-orders", icon: ShoppingCart, label: "Supplier Orders" },
    ...(isOwner ? [
      { to: "/admin/supplier-import", icon: Truck, label: "Supplier Import" },
    ] : []),
    { to: "/admin/discounts", icon: Tag, label: "Discounts" },
    { to: "/admin/text-to-buy", icon: MessageSquareText, label: "Text-to-Buy", highlight: true },
    { to: "/admin/cashiers", icon: Users, label: "Cashiers" },
    { to: "/admin/sales-reps", icon: BadgeCheck, label: "Sales Reps" },
    ...(isOwner ? [
      { to: "/admin/staff", icon: ShieldCheck, label: "Staff & Roles" },
      { to: "/admin/audit", icon: ScrollText, label: "Audit Log" },
    ] : []),
    { to: "/pos", icon: Banknote, label: "POS · Counter", highlight: true, external: true },
    { to: "/admin/settings", icon: Settings, label: "Settings" },
    { to: "/admin/help", icon: HelpCircle, label: "Help & How-To" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-layout">
      <RoleWalkthrough />
      <header className="border-b border-gray-200 bg-white">
        <div className="px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="font-display font-black text-xl tracking-tighter">
              IPVALUE <span className="text-[10px] tracking-[0.3em] text-[#0A1C40] font-bold ml-2">ADMIN</span>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="hover:text-[#0A1C40] inline-flex items-center gap-1.5" data-testid="admin-view-store">
              <ExternalLink className="w-4 h-4" /> <span className="hidden sm:inline">View store</span>
            </Link>
            <span className="text-gray-400 hidden sm:inline">·</span>
            <span className="text-gray-600 hidden sm:inline">{user?.email}</span>
            <button onClick={() => { logout(); nav("/login"); }} className="ml-2 text-gray-600 hover:text-[#DC2626] inline-flex items-center gap-1" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        {/* Mobile nav — horizontal scroll pills (sidebar is hidden below md) */}
        <nav className="md:hidden flex gap-1.5 overflow-x-auto px-4 pb-3 [scrollbar-width:none]" data-testid="admin-mobile-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border ${
                  isActive ? "bg-[#0A1C40] text-white border-[#0A1C40]" : "border-gray-300 text-gray-700"
                }`
              }
            >
              <l.icon className="w-3.5 h-3.5" />
              {l.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <div className="flex">
        <aside className="hidden md:block w-60 min-h-[calc(100vh-4rem)] border-r border-gray-200 bg-white p-5">
          <nav className="space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-[#0A1C40] text-white"
                      : l.highlight
                        ? "text-[#0A1C40] hover:bg-blue-50"
                        : "text-gray-700 hover:bg-gray-100"
                  }`
                }
                data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
